import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { ArrowRight, X } from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { PageHero } from "@/components/PageHero";
import { useLocale } from "@/contexts/LocaleContext";
import { useHeroVideoUrls } from "@/hooks/useHeroVideoUrls";
import { useSavedRecipients } from "@/hooks/useSavedRecipients";
import { DICTS } from "@/lib/i18n";
import giftImage from "@/assets/gift-collection.jpg";
import { getPageHeroImages } from "@/utils/pageHeroImages.functions";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { cachedSSR } from "@/lib/ssrCache";
import { softCtaOnMediaClass } from "@/components/ui/soft-cta";



import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/gifts")({
  headers: publicCacheHeaders,
  // ⚡ TTFB: القراءة مخزَّنة على السيرفر — أوّل بايت لا ينتظر قاعدة البيانات.
  loader: async () =>
    cachedSSR<{ initialHeroImage: string | null }>(
      "gifts-hero",
      async () => {
        const { map } = await getPageHeroImages();
        return { initialHeroImage: map.gifts ?? null };
      },
      { initialHeroImage: null },
    ),
  head: () => ({
    meta: [
      { title: DICTS.en["gifts.meta.title"] },
      { name: "description", content: DICTS.en["gifts.meta.description"] },
      { property: "og:title", content: DICTS.en["gifts.meta.title"] },
      { property: "og:description", content: DICTS.en["gifts.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/gifts` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/gifts` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: PUBLIC_SITE_ORIGIN },
            { "@type": "ListItem", position: 2, name: "Gifting", item: `${PUBLIC_SITE_ORIGIN}/gifts` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Luxury perfume gifting",
          name: DICTS.en["gifts.meta.title"],
          description: DICTS.en["gifts.meta.description"],
          url: `${PUBLIC_SITE_ORIGIN}/gifts`,
          provider: { "@type": "Organization", name: "LECMO", url: PUBLIC_SITE_ORIGIN },
          areaServed: ["AE", "SA", "OM", "IQ"],
        }),
      },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { recipient_id?: string; occasion_id?: string } => {
    const out: { recipient_id?: string; occasion_id?: string } = {};
    if (typeof search.recipient_id === "string") out.recipient_id = search.recipient_id;
    if (typeof search.occasion_id === "string") out.occasion_id = search.occasion_id;
    return out;
  },
  component: GiftsPage,
});

const STEP_KEYS = ["s1", "s2", "s3"] as const;

function GiftsPage() {
  const { t } = useLocale();
  const { recipient_id, occasion_id } = Route.useSearch();
  const { initialHeroImage } = Route.useLoaderData();
  const heroVideo = useHeroVideoUrls("gifts");
  const effectiveHeroImage = heroVideo.poster || initialHeroImage || giftImage;
  const { items: savedRecipients } = useSavedRecipients();

  

  // المستلم المحدَّد من رابط /account → نعرض بانر + نخزّنه في sessionStorage
  // ليلتقطه GiftFlow عند فتحه من بطاقة منتج.
  const prefillRecipient = React.useMemo(
    () => (recipient_id ? savedRecipients.find((r) => r.id === recipient_id) : null),
    [recipient_id, savedRecipients],
  );
  const [bannerDismissed, setBannerDismissed] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (recipient_id) {
      sessionStorage.setItem("gift_prefill_recipient_id", recipient_id);
      if (occasion_id) sessionStorage.setItem("gift_prefill_occasion_id", occasion_id);
    }
  }, [recipient_id, occasion_id]);

  const dismissBanner = () => {
    setBannerDismissed(true);
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("gift_prefill_recipient_id");
      sessionStorage.removeItem("gift_prefill_occasion_id");
    }
  };

  return (
    <SiteShell>
      {prefillRecipient && !bannerDismissed && (
        <div className="border-b border-primary/30 bg-primary/10">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <p className="t-caption text-foreground">
              {t("gift.prefill_banner", {
                name: prefillRecipient.recipient_name || prefillRecipient.recipient_phone,
              })}
            </p>
            <button
              onClick={dismissBanner}
              aria-label={t("gift.prefill_banner_dismiss")}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <PageHero
        pageKey="gifts"
        image={effectiveHeroImage}
        videoUrl={heroVideo.desktop}
        mobileVideoUrl={heroVideo.mobile}
        campaignId={heroVideo.campaignId}
        alt={t("gifts.page.image_alt")}
        title={t("gifts.page.title")}
        subtitle={t("gifts.page.subtitle")}

        cta={
          <Link
            to="/shop"
            className={softCtaOnMediaClass}
          >
            {t("cta.shop_all")} <ArrowRight className="h-4 w-4" />
          </Link>
        }
      />


      <section className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6">
        <div className="grid gap-8 md:grid-cols-3">
          {STEP_KEYS.map((s) => (
            <div key={s} className="border border-border p-8">
              <p className="t-h2 text-primary">{t(`gifts.steps.${s}.n`)}</p>
              <h3 className="mt-3 t-h3">{t(`gifts.steps.${s}.title`)}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t(`gifts.steps.${s}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>



    </SiteShell>
  );
}
